import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { apiClient } from '../utils/api';
import { recruitmentTheme, themeClasses, getStatusColor } from '../styles/theme';
import { StatusBadge, candidateStatusOptions, taskStatusOptions, jobStatusOptions } from '../components/ui/StatusBadge';
import { cn } from '../utils';
import {
  Users,
  Briefcase,
  Calendar,
  CheckSquare,
  CheckCircle,
  TrendingUp,
  UserCheck,
  Clock,
  AlertCircle,
  Search,
  Plus,
  User,
  Settings,
  BarChart3,
  ArrowRight,
  FolderOpen,
  ListChecks,
  Upload,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockCandidates, mockAppointments } from '../data/mockData';
import { navigateWithAction, NavigationActions } from '../utils/navigationUtils';
import CandidateTable from '../components/tables/CandidateTable';
import TaskTable from '../components/tables/TaskTable';
import JobsTable from '../components/tables/JobsTable';
import { LocalStorageManager } from '../utils/localStorage';
import CandidateStatusBadge from '../components/ui/CandidateStatusBadge';
import { Candidate, Job } from '../types';
import { subscribeToTasks, updateTask as updateTaskInFirebase, Task } from '../services/taskService';
import { subscribeToCandidates } from '../services/candidateService';
import { subscribeToJobs } from '../services/jobService';
import { subscribeToAppointments } from '../services/appointmentService';

interface DashboardStats {
  totalCandidates: number;
  candidatesByStage: Record<string, number>;
  activeJobs: number;
  todaysAppointments: number;
  pendingTasks: number;
}

interface DashboardCandidate {
  _id: string;
  name: string;
  email: string;
  phone: string;
  jobTitle?: string;
  company?: string;
  salary?: number;
  stage: string;
  countryApplyingTo?: string;
  passportNumber?: string;
  createdAt: string;
}

// Use Firebase Task interface
type DashboardTask = Task;

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { showNotification, notifyBadgeChange } = useNotifications();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalCandidates: 0,
    candidatesByStage: {},
    activeJobs: 0,
    todaysAppointments: 0,
    pendingTasks: 0
  });
  const [recentCandidates, setRecentCandidates] = useState<DashboardCandidate[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<DashboardTask[]>([]);
  const [recentLocalCandidates, setRecentLocalCandidates] = useState<Candidate[]>([]);
  const [recentLocalJobs, setRecentLocalJobs] = useState<Job[]>([]);
  const [recentLocalTasks, setRecentLocalTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQuickActions, setShowQuickActions] = useState(false); // Default to hidden

  useEffect(() => {
    let unsubscribeTasks: (() => void) | null = null;
    let unsubscribeCandidates: (() => void) | null = null;
    let unsubscribeJobs: (() => void) | null = null;
    let unsubscribeAppointments: (() => void) | null = null;
    
    // Setup all Firebase subscriptions
    const setupAllSubscriptions = async () => {
      try {
        // Subscribe to candidates
        unsubscribeCandidates = await subscribeToCandidates((candidates) => {
          console.log('📊 Dashboard: Received', candidates.length, 'candidates from Firebase');
          setRecentLocalCandidates(candidates);
          
          // Update stats
          const candidatesByStage: Record<string, number> = {};
          candidates.forEach(candidate => {
            candidatesByStage[candidate.stage] = (candidatesByStage[candidate.stage] || 0) + 1;
          });
          
          setStats(prev => ({
            ...prev,
            totalCandidates: candidates.length,
            candidatesByStage
          }));
          
          // Set recent candidates
          const recent = candidates
            .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
            .slice(0, 5)
            .map(c => ({
              _id: c.id || c._id || '',
              name: c.name,
              email: c.email,
              phone: c.phone,
              jobTitle: c.jobTitle,
              company: c.company,
              salary: c.salary,
              stage: c.stage,
              countryApplyingTo: c.countryApplyingTo,
              passportNumber: c.passportNumber,
              createdAt: c.createdAt || new Date().toISOString()
            }));
          setRecentCandidates(recent);
        }, console.error);

        // Subscribe to jobs
        unsubscribeJobs = subscribeToJobs((jobs) => {
          console.log('📊 Dashboard: Received', jobs.length, 'jobs from Firebase');
          setRecentLocalJobs(jobs);
          setStats(prev => ({
            ...prev,
            activeJobs: jobs.length
          }));
        });

        // Subscribe to appointments
        unsubscribeAppointments = subscribeToAppointments((appointments) => {
          console.log('📊 Dashboard: Received', appointments.length, 'appointments from Firebase');
          const today = new Date().toISOString().split('T')[0];
          const todaysCount = appointments.filter(apt => apt.date === today).length;
          setStats(prev => ({
            ...prev,
            todaysAppointments: todaysCount
          }));
        });

        // Subscribe to tasks
        unsubscribeTasks = await subscribeToTasks((firebaseTasks) => {
          console.log('📊 Dashboard: Received', firebaseTasks.length, 'tasks from Firebase');
          setRecentLocalTasks(firebaseTasks);
          
          const pendingCount = firebaseTasks.filter(task => task.status === 'PENDING').length;
          setStats(prev => ({
            ...prev,
            pendingTasks: pendingCount
          }));
          
          const recentTasks = firebaseTasks
            .sort((a, b) => new Date(b.createdAt?.seconds * 1000 || 0).getTime() - new Date(a.createdAt?.seconds * 1000 || 0).getTime())
            .slice(0, 5);
          setUpcomingTasks(recentTasks);
        }, console.error);
        
        setLoading(false);
      } catch (error) {
        console.error('📊 Dashboard: Failed to setup subscriptions:', error);
        setLoading(false);
      }
    };
    
    setupAllSubscriptions();
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith('recruitment_crm_') || e.key === 'generatedJobs' || e.key === 'addedCandidates' || e.key === 'cvBuilderCandidates') {
        console.log('📊 Dashboard: Storage change detected for key:', e.key);
        loadQuickViewData();
      }
    };

    // Listen for comprehensive data update events for instant sync
    const handleDataUpdates = (event: CustomEvent) => {
      console.log('📊 Dashboard: Data update event received:', event.type);
      loadQuickViewData();
    };

    window.addEventListener('storage', handleStorageChange);
    // Add comprehensive event listeners for instant updates
    window.addEventListener('candidatesUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateAdded', handleDataUpdates as EventListener);
    window.addEventListener('candidateUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateDeleted', handleDataUpdates as EventListener);
    window.addEventListener('jobsUpdated', handleDataUpdates as EventListener);
    window.addEventListener('jobAdded', handleDataUpdates as EventListener);
    window.addEventListener('jobUpdated', handleDataUpdates as EventListener);
    window.addEventListener('jobDeleted', handleDataUpdates as EventListener);
    window.addEventListener('appointmentsUpdated', handleDataUpdates as EventListener);
    window.addEventListener('appointmentAdded', handleDataUpdates as EventListener);
    window.addEventListener('appointmentUpdated', handleDataUpdates as EventListener);
    window.addEventListener('appointmentDeleted', handleDataUpdates as EventListener);
    
    return () => {
      console.log('📊 Dashboard: Cleaning up all Firebase subscriptions...');
      if (unsubscribeTasks) unsubscribeTasks();
      if (unsubscribeCandidates) unsubscribeCandidates();
      if (unsubscribeJobs) unsubscribeJobs();
      if (unsubscribeAppointments) unsubscribeAppointments();
      window.removeEventListener('storage', handleStorageChange);
      // Remove all data update event listeners
      window.removeEventListener('candidatesUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateAdded', handleDataUpdates as EventListener);
      window.removeEventListener('candidateUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateDeleted', handleDataUpdates as EventListener);
      window.removeEventListener('jobsUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('jobAdded', handleDataUpdates as EventListener);
      window.removeEventListener('jobUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('jobDeleted', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentsUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentAdded', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentDeleted', handleDataUpdates as EventListener);
    };
  }, []);

  const loadQuickViewData = () => {
    try {
      // Load ALL candidates for accurate counts
      const candidates = LocalStorageManager.getCandidates();
      setRecentLocalCandidates(candidates); // Keep all for counts
      
      // Load ALL jobs for accurate counts  
      const jobs = LocalStorageManager.getJobs();
      setRecentLocalJobs(jobs); // Keep all for counts

      // Tasks are now handled by Firebase subscription in useEffect
    } catch (error) {
      console.error('Failed to load quick view data:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setStats({
        totalCandidates: 245,
        candidatesByStage: {
          'Onboarding': 45,
          'Interviewing': 32,
          'Offer': 18,
          'Hired': 67,
          'Rejected': 83
        },
        activeJobs: 15,
        todaysAppointments: 8,
        pendingTasks: 12
      });
      
      setRecentCandidates([...mockCandidates]);
      // Tasks are now handled by Firebase subscription in useEffect
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleTaskStatusChange = (taskId: string, newStatus: string) => {
    const mappedStatus = newStatus.toUpperCase().replace('-', '_') as Task['status'];
    const task = recentLocalTasks.find(t => t.id === taskId);
    if (!task) return;

    const oldStatus = task.status;
    const taskTitle = task.title || 'Unknown Task';
    
    // Update Firebase - the real-time listener will update the UI
    updateTaskInFirebase(taskId, { status: mappedStatus })
      .then(() => {
        console.log(`📊 Dashboard: Successfully updated task ${taskTitle} status: ${oldStatus} → ${mappedStatus}`);
        notifyBadgeChange('Dashboard', `${taskTitle} Status`, oldStatus, mappedStatus);
      })
      .catch(error => {
        console.error('❌ Dashboard: Failed to update task status in Firebase:', error);
      });
  };

  const handleJobStatusChange = (jobId: string, newStatus: string) => {
    const job = recentLocalJobs.find(j => j.id === jobId);
    if (!job) return;

    const oldStatus = job.status;
    const validStatus = newStatus as 'Active' | 'Closed' | 'Draft';
    
    LocalStorageManager.updateJob(jobId, { status: validStatus });
    
    setRecentLocalJobs(prev => prev.map(j => 
      j.id === jobId ? { ...j, status: validStatus } : j
    ));

    notifyBadgeChange('Dashboard', `${job.title} Status`, oldStatus, newStatus);
    
    window.dispatchEvent(new CustomEvent('jobStatusChanged', { 
      detail: { id: jobId, oldStatus, newStatus, jobTitle: job.title }
    }));
    
    // Dispatch comprehensive update events for instant sync across all pages
    window.dispatchEvent(new CustomEvent('jobUpdated', { 
      detail: { id: jobId, oldStatus, newStatus, jobTitle: job.title }
    }));
    window.dispatchEvent(new CustomEvent('jobsUpdated', { 
      detail: { action: 'statusChanged', jobId: jobId, jobTitle: job.title }
    }));

    console.log(`📊 Updated job ${job.title} status on Dashboard: ${oldStatus} → ${newStatus}`);
  };

  const getDashboardContent = () => {
    switch (user?.role) {
      case 'Admin':
        return renderAdminDashboard();
      case 'Receptionist':
        return renderReceptionistDashboard();
      case 'Broker':
        return renderBrokerDashboard();
      default:
        return renderDefaultDashboard();
    }
  };

  const renderAdminDashboard = () => {
    // Get accurate live counts for all data sources
    const totalCandidates = LocalStorageManager.getCandidates().length;
    const totalJobs = LocalStorageManager.getJobs().length;
    const totalTasks = recentLocalTasks.length; // Live Firebase data
    const totalAppointments = LocalStorageManager.getAppointments().length;

    const formatCurrency = (amount?: number) => {
      if (!amount) return 'N/A';
      return new Intl.NumberFormat('en-KW', {
        style: 'currency',
        currency: 'KWD'
      }).format(amount);
    };

    const formatDate = (dateString?: string) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    return (
      <div className="space-y-4 lg:space-y-6">

        {/* Action Buttons */}
        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#8b6914]">Quick Actions</CardTitle>
                <CardDescription className="text-gray-600">Access key functions instantly</CardDescription>
              </div>
              <button
                onClick={() => setShowQuickActions(!showQuickActions)}
                className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm px-3 py-1`}
              >
                {showQuickActions ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    <span>Hide</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    <span>Show</span>
                  </>
                )}
              </button>
            </div>
          </CardHeader>
          {showQuickActions && (
            <CardContent>
              <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              <button 
                onClick={() => navigateWithAction(navigate, NavigationActions.searchCandidates)}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Search className="mr-2 h-4 w-4" /> Search Candidate
              </button>
              <button 
                onClick={() => navigateWithAction(navigate, NavigationActions.addCandidate)}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Plus className="mr-2 h-4 w-4" /> Add Candidate
              </button>
              <button 
                onClick={() => navigateWithAction(navigate, NavigationActions.createCV)}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <User className="mr-2 h-4 w-4" /> Create CV
              </button>
              <button 
                onClick={() => {
                  navigate('/cv-builder');
                  // Use setTimeout to ensure navigation completes before triggering events
                  setTimeout(() => {
                    // Trigger event to auto-select the file upload button in Quick autofill section
                    window.dispatchEvent(new CustomEvent('cvBuilderAction', {
                      detail: { action: 'autoSelectFileUpload' }
                    }));
                  }, 100);
                }}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Upload className="mr-2 h-4 w-4" /> Quick CV Import
              </button>
              <button 
                onClick={() => navigateWithAction(navigate, NavigationActions.viewJobs)}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Briefcase className="mr-2 h-4 w-4" /> View Jobs
              </button>
              <button 
                onClick={() => navigateWithAction(navigate, NavigationActions.jobGenerator)}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Plus className="mr-2 h-4 w-4" /> Jobs Generator
              </button>
              <button 
                onClick={() => navigate('/tasks')}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Clock className="mr-2 h-4 w-4" /> Upcoming Tasks
              </button>
              <button 
                onClick={() => navigate('/appointments')}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Calendar className="mr-2 h-4 w-4" /> View Appointments
              </button>
              <button 
                onClick={() => navigateWithAction(navigate, NavigationActions.assignTask)}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <CheckSquare className="mr-2 h-4 w-4" /> Assign Tasks
              </button>
              <button 
                onClick={() => navigate('/reports')}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <BarChart3 className="mr-2 h-4 w-4" /> Reports
              </button>
              <button 
                onClick={() => navigate('/documents')}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <FolderOpen className="mr-2 h-4 w-4" /> Documents
              </button>
              <button 
                onClick={() => {
                  navigate('/documents');
                  // Use setTimeout to ensure navigation completes before triggering events
                  setTimeout(() => {
                    // Trigger event to set CVs tab as active
                    window.dispatchEvent(new CustomEvent('documentsPageAction', {
                      detail: { action: 'openUploadedCVs' }  
                    }));
                  }, 100);
                }}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Upload className="mr-2 h-4 w-4" /> Uploaded CVs
              </button>
              <button 
                onClick={() => navigate('/settings')}
                className={`${recruitmentTheme.components.button.secondary} w-full justify-start text-xs sm:text-sm`}
              >
                <Settings className="mr-2 h-4 w-4" /> Settings
              </button>
            </div>
          </CardContent>
          )}
        </div>

        {/* Recent Candidates */}
        <div className={recruitmentTheme.components.card}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-[#8b6914]" />
                <CardTitle className="text-[#8b6914]">Recent Candidates</CardTitle>
                <span className="text-sm text-gray-500">({recentLocalCandidates.length} of {totalCandidates})</span>
              </div>
              <button 
                onClick={() => navigate('/candidates')}
                className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
              >
                <span>View All Candidates</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {recentLocalCandidates.length === 0 ? (
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No candidates found</h3>
                <p className="mt-1 text-sm text-gray-500">Add your first candidate to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLocalCandidates.slice(0, 5).map((candidate, index) => (
                  <div key={candidate.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center space-x-3 flex-1">
                      <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                        {index + 1}.
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                          <CandidateStatusBadge
                            currentStatus={candidate.stage}
                            onStatusChange={(newStage) => {
                              const oldStage = candidate.stage;
                              console.log(`🔄 Dashboard candidate status change: ${candidate.name} from ${oldStage} to ${newStage}`);
                              
                              LocalStorageManager.updateCandidate(candidate.id, { stage: newStage });
                              loadQuickViewData();
                              
                              showNotification({
                                type: 'candidate',
                                title: 'Candidate Status Updated',
                                message: `${candidate.name} moved from "${oldStage}" to "${newStage}"`,
                                priority: 'medium'
                              });

                              window.dispatchEvent(new CustomEvent('candidateStatusChanged', { 
                                detail: { id: candidate.id, oldStatus: oldStage, newStatus: newStage, candidateName: candidate.name }
                              }));
                              
                              // Dispatch comprehensive update events for instant sync across all pages
                              window.dispatchEvent(new CustomEvent('candidateUpdated', { 
                                detail: { id: candidate.id, oldStatus: oldStage, newStatus: newStage, candidateName: candidate.name }
                              }));
                              window.dispatchEvent(new CustomEvent('candidatesUpdated', { 
                                detail: { action: 'statusChanged', candidateId: candidate.id, candidateName: candidate.name }
                              }));

                              console.log(`✅ Dashboard candidate ${candidate.name} status: ${oldStage} → ${newStage}`);
                            }}
                            size="sm"
                          />
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                          <span>{candidate.jobTitle || 'No position'}</span>
                          <span>{candidate.company || 'No company'}</span>
                          <span>{formatCurrency(candidate.salary)}</span>
                        </div>
                        <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                          <span>{candidate.email}</span>
                          <span>•</span>
                          <span>{candidate.phone}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </div>

        {/* Recent Jobs */}
        <div className={recruitmentTheme.components.card}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Briefcase className="w-5 h-5 text-[#8b6914]" />
                <CardTitle className="text-[#8b6914]">Recent Jobs</CardTitle>
                <span className="text-sm text-gray-500">({recentLocalJobs.length} of {totalJobs})</span>
              </div>
              <button 
                onClick={() => navigate('/jobs')}
                className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
              >
                <span>View All Jobs</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {recentLocalJobs.length === 0 ? (
              <div className="text-center py-8">
                <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
                <p className="mt-1 text-sm text-gray-500">Add your first job posting to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLocalJobs.slice(0, 5).map((job, index) => (
                  <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center space-x-3 flex-1">
                      <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                        {index + 1}.
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{job.title}</h4>
                          <StatusBadge
                            currentStatus={job.status.toLowerCase()}
                            options={jobStatusOptions}
                            onStatusChange={(newStatus) => handleJobStatusChange(job.id, newStatus.charAt(0).toUpperCase() + newStatus.slice(1))}
                            size="sm"
                          />
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                          <span>{job.company}</span>
                          <span>{job.location}</span>
                          <span>{job.type}</span>
                        </div>
                        <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                          <span>{job.salaryRange.min} - {job.salaryRange.max} {job.salaryRange.currency}</span>
                          <span>•</span>
                          <span>Posted {formatDate(job.postedDate)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </div>

        {/* Recent Tasks */}
        <div className={recruitmentTheme.components.card}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CheckSquare className="w-5 h-5 text-[#8b6914]" />
                <CardTitle className="text-[#8b6914]">Recent Tasks</CardTitle>
                <span className="text-sm text-gray-500">({recentLocalTasks.length} of {totalTasks})</span>
              </div>
              <button 
                onClick={() => navigate('/tasks')}
                className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
              >
                <span>View All Tasks</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {recentLocalTasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckSquare className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
                <p className="mt-1 text-sm text-gray-500">Add your first task to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLocalTasks.slice(0, 5).map((task, index) => (
                    <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                          {index + 1}.
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            <StatusBadge
                              currentStatus={task.status.toLowerCase().replace('_', '-')}
                              options={taskStatusOptions}
                              onStatusChange={(newStatus) => task.id && handleTaskStatusChange(task.id, newStatus)}
                              size="sm"
                            />
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                            <span>{task.assignedTo}</span>
                            <span>{task.category}</span>
                            <span>{task.priority} Priority</span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                            <span>Due: {task.dueDate}</span>
                            <span>•</span>
                            <span>By: {task.assignedBy}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </div>

        {/* Task Summary Stats - Live Sync */}
        <div className={recruitmentTheme.components.card}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CheckSquare className="w-5 h-5 text-[#8b6914]" />
                <CardTitle className="text-[#8b6914]">Task Summary</CardTitle>
                <span className="text-sm text-gray-500">(Live Data)</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-green-50 rounded-lg border">
                <div className="text-2xl font-bold text-green-600">
                  {recentLocalTasks.filter(t => t.status === 'COMPLETED').length}
                </div>
                <div className="text-sm text-green-700">Completed</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg border">
                <div className="text-2xl font-bold text-yellow-600">
                  {recentLocalTasks.filter(t => t.status === 'PENDING').length}
                </div>
                <div className="text-sm text-yellow-700">Pending</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg border">
                <div className="text-2xl font-bold text-blue-600">
                  {recentLocalTasks.filter(t => t.status === 'IN_PROGRESS').length}
                </div>
                <div className="text-sm text-blue-700">In Progress</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg border">
                <div className="text-2xl font-bold text-red-600">
                  {recentLocalTasks.filter(t => t.status === 'OVERDUE').length}
                </div>
                <div className="text-sm text-red-700">Overdue</div>
              </div>
            </div>
          </CardContent>
        </div>
      </div>
    );
  };

  const renderReceptionistDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Today's Appointments</CardTitle>
            <Calendar className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">{stats.todaysAppointments}</div>
            <p className="text-xs text-gray-600">Next at 2:00 PM</p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">New Candidates</CardTitle>
            <Users className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">3</div>
            <p className="text-xs text-gray-600">Walk-ins today</p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Pending Tasks</CardTitle>
            <Clock className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">2</div>
            <p className="text-xs text-gray-600">Urgent</p>
          </CardContent>
        </div>
      </div>

      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <CardTitle className="text-[#8b6914]">Today's Schedule</CardTitle>
          <CardDescription className="text-gray-600">Upcoming appointments and tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-4 p-4 bg-cream-warm rounded-lg border border-gray-200">
              <Clock className="h-4 w-4 text-[#8b6914]" />
              <div>
                <p className="font-medium text-[#8b6914]">John Smith Interview</p>
                <p className="text-sm text-gray-600">2:00 PM - Marketing Position</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-4 bg-cream-warm rounded-lg border border-gray-200">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              <div>
                <p className="font-medium text-[#8b6914]">Prepare meeting room</p>
                <p className="text-sm text-gray-600">Client presentation setup</p>
              </div>
            </div>
          </div>
        </CardContent>
      </div>
    </div>
  );

  const renderBrokerDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">My Leads</CardTitle>
            <TrendingUp className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">15</div>
            <p className="text-xs text-gray-600">5 hot prospects</p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Placements</CardTitle>
            <UserCheck className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">8</div>
            <p className="text-xs text-gray-600">This month</p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Commission</CardTitle>
            <TrendingUp className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">KSH 45,000</div>
            <p className="text-xs text-gray-600">+15% from last month</p>
          </CardContent>
        </div>
      </div>

      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <CardTitle className="text-[#8b6914]">Recent Activity</CardTitle>
          <CardDescription className="text-gray-600">Your latest client interactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-4 p-4 bg-cream-warm rounded-lg border border-gray-200">
              <UserCheck className="h-4 w-4 text-green-500" />
              <div>
                <p className="font-medium text-[#8b6914]">Sarah Johnson placed at Global Finance</p>
                <p className="text-sm text-gray-600">2 hours ago</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-4 bg-cream-warm rounded-lg border border-gray-200">
              <Clock className="h-4 w-4 text-orange-500" />
              <div>
                <p className="font-medium text-[#8b6914]">Follow up with Innovatech</p>
                <p className="text-sm text-gray-600">Due tomorrow</p>
              </div>
            </div>
          </div>
        </CardContent>
      </div>
    </div>
  );

  const renderDefaultDashboard = () => (
    <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-[#8b6914]">Welcome to Naim Investments CRM</h2>
      <p className="text-gray-600 mt-2">Your recruitment management system</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cream-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {getDashboardContent()}
    </div>
  );
};

export default DashboardPage;
